import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button, HelpTooltip, Input, Select } from '@fiado/react-kit';
import { Label } from '../../../components/ui/label';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { ApiError } from '../../../api/errors';
import { generateIdempotencyKey } from '../../../lib/idempotency';
import { PendingInfraModal } from './PendingInfraModal';
import { EditTenantSecuritySection } from './EditTenantSecuritySection';
import { securityCannotSave, type SecurityDraft } from '../../../lib/securityPolicy';
import { useApplication, useApplications } from '../../../hooks/useApplications';
import type { CreateTenantRequestLocal } from '../../../api/types/PlatformLocal';
import type { MfaMethodLocal } from '../../../api/types/TenantLocal';

// DEC-RBAC-064: si todavía no hay app elegida (o su detalle no resolvió), arrancamos secure-by-default:
// 2FA prendido con TOTP. Al resolver la app, se reemplaza por `app.defaults.securityPolicy` (salvo que el
// usuario ya haya tocado la sección a mano).
const DEFAULT_SECURITY_DRAFT: SecurityDraft = {
    mfaEnabled: true,
    methodsAllowed: ['TOTP'] satisfies MfaMethodLocal[],
};

// Única región operativa de la plataforma (tablas DDB + pools Cognito). El alta no la deja elegir.
const FIXED_REGION = 'us-east-1';

// Espeja CreateTenantRequest (backoffice.gen.ts). tenantId en minúsculas (id lógico). tablePrefix es
// el PREFIJO de las tablas DDB del silo (<prefix>SharedX_GT) → respeta el case real de las tablas
// desplegadas (PascalCase: SureKeep, Fiado, Agents).
//
// DEC-RBAC-057: el alta de tenant YA NO crea admin (el primer admin se crea después por el flujo
// normal de usuarios). Por eso se quitaron del schema/form los campos `adminEmail`/`adminName` y la
// sección "Admin inicial".
const schema = z.object({
    tenantId: z
        .string()
        .min(1, 'Requerido')
        .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
    displayName: z.string().min(1, 'Requerido').max(120),
    tablePrefix: z
        .string()
        .min(1, 'Requerido')
        .regex(/^[A-Za-z0-9_]+$/, 'Solo letras, números y guion bajo'),
    // Región fija: us-east-1 es la única región operativa (tablas DDB y pools Cognito viven ahí). No es
    // un campo editable — el form la fija y el body siempre viaja region: 'us-east-1'. Se muestra
    // read-only para que el usuario la vea sin poder tipearla.
    region: z.literal(FIXED_REGION),
    // applicationId: la aplicación (plantilla data-driven) de la que se onboardea el tenant — define
    // niveles, techo de permisos (catálogo), roles seed y defaults. Reemplaza al tenantType legacy
    // (DEC-RBAC-031). El backend aplica el techo/seedRoles/defaults de la app al onboardear.
    applicationId: z.string().min(1, 'Elige una aplicación'),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Alta de tenant del módulo Platform. Form único de datos del tenant (sin admin inicial —
 * DEC-RBAC-057: el primer admin se crea después por el flujo normal de usuarios). Submit provisiona el
 * Cognito pool y dispara el pre-check de infra:
 *  - `status === 'active'` → tenant operativo → toast de éxito.
 *  - `status === 'pending-infra'` (DEC-RBAC-058) → faltan tablas DDB del silo → abre el
 *    `PendingInfraModal` con la lista de tablas a pedirle a infra + botón "Revisar infra"
 *    (re-corre `verify-infra`).
 *
 * Usa el overlay inline (no Radix Dialog) — patrón testeable del repo (ver RoleAssignmentDialog).
 */
export function CreateTenantWizard({ open, onOpenChange }: Props) {
    const queryClient = useQueryClient();
    // Si el alta vuelve `pending-infra`, guardamos tenantId + las tablas faltantes para el modal de
    // pendiente-de-infra. `null` = sin pendiente (alta `active` o todavía sin enviar).
    const [pending, setPending] = useState<{
        tenantId: string;
        tenantName: string;
        infraRequirements: string[];
    } | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            tenantId: '',
            displayName: '',
            tablePrefix: '',
            region: FIXED_REGION,
            applicationId: '',
        },
    });
    const {
        data: applications,
        isPending: applicationsPending,
        isError: applicationsError,
    } = useApplications();
    // Select controlado del kit (value/onChange) → setValue. Mismo patrón que EditTenantModal.
    const selectedApplicationId = useWatch({ control: form.control, name: 'applicationId' });
    // Detalle de la app elegida (la LISTA no trae `defaults`; el detalle sí → `defaults.securityPolicy`).
    const { data: selectedApp } = useApplication(selectedApplicationId || undefined);

    // ── Métodos 2FA (DEC-RBAC-064) ──────────────────────────────────────────────────────────────────
    // Punto inicial de la política 2FA del tenant. Se PRE-POBLA con los métodos de la APP elegida y queda
    // editable antes de crear. Derivado en render (no por useEffect → sin cascading renders): mientras el
    // usuario no haya tocado la sección (`securityEdit === null`), gana el pre-poblado de la app; en cuanto
    // la edita, su elección manda y cambiar de app ya NO la pisa. Mismo patrón que EditTenantModal.
    const [securityEdit, setSecurityEdit] = useState<SecurityDraft | null>(null);
    // Pre-poblado desde la app resuelta. Secure-by-default si la app no trae defaults: 2FA prendido + TOTP.
    const appSecurity = selectedApp?.defaults.securityPolicy;
    const securityFromApp = useMemo<SecurityDraft>(
        () => ({
            mfaEnabled: appSecurity?.mfaEnabled ?? DEFAULT_SECURITY_DRAFT.mfaEnabled,
            methodsAllowed: appSecurity?.methodsAllowed ?? DEFAULT_SECURITY_DRAFT.methodsAllowed,
        }),
        [appSecurity?.mfaEnabled, appSecurity?.methodsAllowed],
    );
    const security = securityEdit ?? securityFromApp;
    // DEC-RBAC-087: el identificador principal (heredado de la app) manda el invariante anti-lockout —
    // el tenant no puede quedar con el único OTP siendo el del secundario.
    const appPrincipal = selectedApp?.defaults.principalIdentifier;

    const mut = useMutation({
        // Idempotency-Key FRESCA por submit: corregir y reintentar no choca con BODY_DIFFERS.
        mutationFn: (body: CreateTenantRequestLocal) =>
            platformApi.createTenant(body, { idempotencyKey: generateIdempotencyKey() }),
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
            onOpenChange(false);
            // Reset de la sección 2FA: vuelve a pre-poblarse desde la app en el próximo alta.
            setSecurityEdit(null);
            if (res.status === 'pending-infra') {
                // Faltan tablas del silo: el tenant quedó creado pero no operativo. Mostramos la lista
                // para pedirle a infra + el botón de re-verificación.
                setPending({
                    tenantId: res.tenantId,
                    tenantName: form.getValues('displayName'),
                    infraRequirements: res.infraRequirements ?? [],
                });
            } else {
                toast.success('Organización creada');
            }
            form.reset();
        },
        onError: (err) => {
            const strategy = handleApiError(err);
            if (strategy === 'INLINE' && err instanceof ApiError) {
                if (err.code === 'TENANT_ALREADY_EXISTS') {
                    form.setError('tenantId', { message: 'Esa organización ya existe' });
                }
            }
        },
    });

    return (
        <>
            {open && (
                <div
                    role="dialog"
                    aria-label="Crear organización"
                    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16"
                >
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    />
                    <div className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold">Crear organización</h2>
                                <p className="text-sm text-muted-foreground">
                                    Da de alta la organización y su acceso. El administrador inicial
                                    se crea después, desde la gestión de usuarios.
                                </p>
                            </div>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="-mr-1 rounded-md p-1.5 text-foreground/70 transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={() => {
                                    onOpenChange(false);
                                }}
                            >
                                <X className="size-4" aria-hidden="true" />
                            </button>
                        </div>

                        <form
                            onSubmit={(e) => {
                                void form.handleSubmit((v) => {
                                    // DEC-RBAC-064: el alta lleva el punto inicial de 2FA. El backend lo usa
                                    // como punto de partida (gana campo a campo sobre el default de la app).
                                    mut.mutate({
                                        ...v,
                                        securityPolicy: {
                                            mfaEnabled: security.mfaEnabled,
                                            methodsAllowed: security.methodsAllowed,
                                        },
                                    });
                                })(e);
                            }}
                            className="space-y-5"
                        >
                            <section className="space-y-4">
                                <h3 className="text-sm font-semibold text-muted-foreground">
                                    Datos de la organización
                                </h3>
                                <Input
                                    label="Identificador de la organización"
                                    {...form.register('tenantId')}
                                    error={form.formState.errors.tenantId?.message}
                                />
                                <Input
                                    label="Nombre"
                                    {...form.register('displayName')}
                                    error={form.formState.errors.displayName?.message}
                                />
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-1">
                                        <Label htmlFor="tablePrefix">Prefijo de tablas</Label>
                                        <HelpTooltip content="Prefijo de las tablas de la organización (ej. SureKeep). Respeta mayúsculas y minúsculas de las tablas existentes. No se puede editar después." />
                                    </div>
                                    <Input
                                        id="tablePrefix"
                                        {...form.register('tablePrefix')}
                                        error={form.formState.errors.tablePrefix?.message}
                                    />
                                </div>
                                {/* Región fija (us-east-1): read-only, no editable. Se usa readOnly y
                                    no disabled para que el valor no quede atenuado (el kit baja la
                                    opacidad de los inputs disabled) y siga legible. El register mantiene
                                    el valor en el form → el body viaja region: 'us-east-1'. */}
                                <Input
                                    label="Región"
                                    readOnly
                                    hint="Única región operativa. No se puede cambiar."
                                    className="cursor-not-allowed bg-muted"
                                    {...form.register('region')}
                                    error={form.formState.errors.region?.message}
                                />
                                {/* Aplicación: la plantilla data-driven (niveles + techo de permisos +
                                    roles seed + defaults) de la que se onboardea el tenant. Reemplaza al
                                    tipo de tenant legacy. Select controlado del kit → setValue. */}
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-1">
                                        <Label>Aplicación</Label>
                                        <HelpTooltip content="La aplicación (plantilla) de la que hereda esta organización: niveles disponibles, catálogo de permisos y roles preconfigurados." />
                                    </div>
                                    <Select
                                        options={[...(applications ?? [])]
                                            .sort((a, b) =>
                                                a.displayName.localeCompare(b.displayName, 'es'),
                                            )
                                            .map((a) => ({
                                                value: a.applicationId,
                                                label: a.displayName,
                                            }))}
                                        value={selectedApplicationId}
                                        onChange={(value) => {
                                            form.setValue('applicationId', value, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                        }}
                                        placeholder={
                                            applicationsPending
                                                ? 'Cargando aplicaciones…'
                                                : 'Elige una aplicación'
                                        }
                                        disabled={applicationsPending || applicationsError}
                                        error={
                                            applicationsError
                                                ? 'No pudimos cargar las aplicaciones. Cierra y vuelve a intentarlo.'
                                                : form.formState.errors.applicationId?.message
                                        }
                                    />
                                </div>
                            </section>

                            {/* DEC-RBAC-064: punto inicial de la política 2FA del tenant. Se pre-puebla con
                                los métodos de la APP elegida (app.defaults.securityPolicy) y queda editable
                                antes de crear. Reusa la sección controlada del modal de editar tenant. */}
                            <section className="space-y-4">
                                <h3 className="text-sm font-semibold text-muted-foreground">
                                    Métodos de verificación
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    Pre-cargados desde la aplicación elegida. Puedes ajustarlos para
                                    esta organización antes de crearla.
                                </p>
                                <EditTenantSecuritySection
                                    value={security}
                                    onChange={setSecurityEdit}
                                    principalIdentifier={appPrincipal}
                                />
                            </section>

                            {/* DEC-060: emailDomain retirado del modelo (el login es por membresía, no por
                                dominio de email). DEC-RBAC-057: la sección "Admin inicial" se quitó (el
                                alta ya no crea admin). */}
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        onOpenChange(false);
                                    }}
                                >
                                    Cancelar
                                </Button>
                                {/* DEC-RBAC-064: bloquea el alta con 2FA prendido y 0 métodos (la
                                    sección muestra el error rojo); mismo gate que EditTenantModal /
                                    PlatformSecurityEditor. `security` = securityEdit ?? securityFromApp. */}
                                <Button
                                    type="submit"
                                    isLoading={mut.isPending}
                                    disabled={
                                        mut.isPending || securityCannotSave(security, appPrincipal)
                                    }
                                >
                                    Crear organización
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {pending && (
                <PendingInfraModal
                    open
                    onOpenChange={(o) => {
                        if (!o) setPending(null);
                    }}
                    tenantId={pending.tenantId}
                    tenantName={pending.tenantName}
                    infraRequirements={pending.infraRequirements}
                />
            )}
        </>
    );
}
